import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Box,
  Typography,
  IconButton,
  FormControl,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Button,
  Grid,
  Chip,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Check, Create, Visibility, Cancel } from '@mui/icons-material';
import { trim, isEqual, toUpper, set, cloneDeep } from 'lodash';
import { useFirestore } from 'react-redux-firebase';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../actions';
import propTypes from 'prop-types';
import { organizationModel } from '../../../models/organizationModel';
import collections from '../../../constants/FirestoreCollections';
import InsuranceSelectorModal from './inurancesModal';
import { panelLogEvent } from '../../../utils/utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
const onboardingUrl = process.env.REACT_APP_ONBOARDING_URL;
const useStyles = makeStyles((theme) => ({
  appBarSpacer: theme.mixins.toolbar,
  organizationItem: {
    height: '80px',
    overflow: 'hidden',
  },
  itemTitle: {
    fontSize: '20px',
  },
  openItem: {
    height: 'auto',
  },
  itemValue: {
    background: theme.palette.primary.main,
    padding: '2px',
    marginLeft: '4px',
  },
  circularProgress: {
    marginInline: theme.spacing(2),
    verticalAlign: 'middle',
  },
  sectionTitle: {
    margin: '32px 0 16px 0',
  },
  serviceTag: {
    padding: '2px 16px',
    borderRadius: '3rem',
    color: theme.palette.white.main,
    marginRight: 8,
  },
  serviceBots: {
    backgroundColor: '#4287f5',
  },
  serviceHealthSoftware: {
    backgroundColor: '#ad33de',
  },
  servicePractices: {
    backgroundColor: '#c9c41c',
  },
  serviceNotifications: {
    backgroundColor: '#47de6f',
  },
}));

function Organization({
  organization,
  mode,
  actualItem,
  handleShow,
  handleEdit,
  handleSubmit,
  organizationNames,
  credentials,
  generateCredentials,
}) {
  const classes = useStyles();
  const [tmpOrganization, setTmpOrganization] = useState({});
  const [areEqual, setAreEqual] = useState(true);
  const [inProgress, setInProgress] = useState(false);
  const [submited, setSubmited] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const firestore = useFirestore();
  const appDispatch = useDispatch();
  const profile = useSelector((state) => state.firebase.profile);

  useEffect(() => {
    if (organization) {
      const { id, ...org } = organization;
      setTmpOrganization(cloneDeep(org));
    }
  }, [organization.id, organization]);

  const name = tmpOrganization && tmpOrganization.name;
  const displayName = tmpOrganization && tmpOrganization.displayName;
  const provider = tmpOrganization && tmpOrganization.provider;
  const healthSoftware = tmpOrganization && tmpOrganization.healthSoftware;
  const businessRules = tmpOrganization && tmpOrganization.businessRules;
  const billingData = tmpOrganization && tmpOrganization.billingData;
  const services = tmpOrganization && tmpOrganization.services;
  const deliveryOnWeekend = tmpOrganization && businessRules?.notificationsRules?.deliveryOnWeekend;
  const daysPriorForDelivery = businessRules?.notificationsRules?.daysProirForDelivery;

  const trimStringsInObject = (obj) => {
    for (const key in obj) {
      if (typeof obj[key] === 'string') {
        obj[key] = obj[key].trim();
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        trimStringsInObject(obj[key]);
      } else if (Array.isArray(obj[key])) {
        obj[key].forEach((item) => {
          if (typeof item === 'string') {
            obj[key] = obj[key].map((str) => str.trim());
          } else if (typeof item === 'object' && item !== null) {
            trimStringsInObject(item);
          }
        });
      }
    }
  };

  const verifyData = () => {
    if (!tmpOrganization.name || !tmpOrganization.displayName) return false;
    if (services && services.healthSoftware) {
      if (
        !tmpOrganization ||
        !tmpOrganization.healthSoftware ||
        !tmpOrganization.healthSoftware.name ||
        !trim(tmpOrganization.healthSoftware.name)
      )
        return false;
    }
    trimStringsInObject(tmpOrganization);
    return true;
  };

  const onSubmit = async () => {
    const verifiedData = verifyData();
    if (!verifiedData) {
      setSubmited(true);
      return;
    }
    const emptyOrganization = {
      ...organizationModel,
      name: trim(tmpOrganization.name) || '',
      displayName: trim(tmpOrganization.name) || '',
      businessRules: {
        ...organizationModel.businessRules,
        ...tmpOrganization.businessRules,
        notificationsRules: {
          ...organizationModel.businessRules?.notificationsRules,
          ...tmpOrganization.businessRules?.notificationsRules,
        },
      },
      healthSoftware: {
        ...organizationModel.healthSoftware,
        ...tmpOrganization.healthSoftware,
      },
      provider: {
        ...organizationModel.provider,
        ...tmpOrganization.provider,
        name: 'gupshup',
      },
      services: {
        ...organizationModel.services,
        ...tmpOrganization.services,
      },
    };
    setInProgress(true);
    if (mode === 'create') {
      if (organizationNames.find((e) => e === toUpper(emptyOrganization.name))) {
        setInProgress(false);
        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: 'Ya existe una organización con el mismo nombre!',
            snackbarSeverity: 'error',
          },
        });
        return;
      }
      const _newItem = { ...emptyOrganization };
      delete _newItem.id;
      axios(`${onboardingUrl}/create-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        data: JSON.stringify({ newOrganization: _newItem }),
      })
        .then(async ({ data }) => {
          appDispatch({
            type: actions.alert,
            alert: {
              snackbarOpen: true,
              snackbarMessage: 'Se agrego exitosamente la organización!',
              snackbarSeverity: 'success',
            },
          });
          setInProgress(false);
          setTmpOrganization({});
          setSubmited(false);
          handleSubmit(organization.id, mode, {
            ...emptyOrganization,
            id: data.organizationId,
          });
          await panelLogEvent({
            type: 'organizations',
            user: { email: profile?.email },
            updatedItem: _newItem,
            logContext: {
              collection: 'organizations',
              docId: data.organizationId,
              action: 'create',
            },
          });
        })
        .catch((err) => {
          window.console.log(err);
          appDispatch({
            type: actions.alert,
            alert: {
              snackbarOpen: true,
              snackbarMessage: 'Ocurrio un error al agregar la organización!',
              snackbarSeverity: 'error',
            },
          });
          setInProgress(false);
        });
    } else {
      const _organization = { ...organization };
      const _tmpOrganization = { ...tmpOrganization };
      delete _organization.id;
      delete _tmpOrganization.id;
      firestore
        .collection(collections.organizations)
        .doc(organization.id)
        .update({
          ..._organization,
          ..._tmpOrganization,
          name: trim(_tmpOrganization.name),
          businessRules: {
            ..._organization.businessRules,
            ..._tmpOrganization.businessRules,
            notificationsRules: {
              ..._organization.businessRules?.notificationsRules,
              ..._tmpOrganization.businessRules?.notificationsRules,
            },
          },
          healthSoftware: { ..._organization.healthSoftware, ..._tmpOrganization.healthSoftware },
          provider: { ..._organization.provider, ..._tmpOrganization.provider },
        })
        .then(async () => {
          appDispatch({
            type: actions.alert,
            alert: {
              snackbarOpen: true,
              snackbarMessage: 'Se ha actualizado la organización!',
              snackbarSeverity: 'success',
            },
          });
          setInProgress(false);
          handleSubmit(organization.id, mode, tmpOrganization);
          await panelLogEvent({
            type: 'organizations',
            user: { email: profile?.email },
            updatedItem: tmpOrganization,
            oldItem: organization,
            logContext: { collection: 'organizations', docId: organization.id, action: 'update' },
          });
        })
        .catch((err) => {
          window.console.log(err);
          appDispatch({
            type: actions.alert,
            alert: {
              snackbarOpen: true,
              snackbarMessage: 'Ocurrio un error al actualizar la organización!',
              snackbarSeverity: 'error',
            },
          });
          setInProgress(false);
        });
    }
  };

  const updateField = (field, value) => {
    const updatedOrganization = cloneDeep(tmpOrganization);
    set(updatedOrganization, field, value);
    setTmpOrganization(updatedOrganization);
  };

  useEffect(() => {
    tmpOrganization.id = organization.id;
    const areEqual = isEqual(organization, tmpOrganization);
    setAreEqual(areEqual);
  }, [tmpOrganization]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  const handleSaveModal = (insurancesToValidate) => {
    updateField('businessRules.gecrosValidation.insurancesToValidate', insurancesToValidate);
    handleCloseModal();
  };
  return (
    <Box
      my={2}
      p={2}
      pt={0}
      boxShadow={1}
      className={`${classes.organizationItem} ${
        actualItem.item === organization.id && classes.openItem
      }`}
    >
      <Box marginY={2} display="flex" alignItems="center">
        {credentials && mode !== 'create' && (
          <IconButton onClick={() => generateCredentials(organization.id)}>
            <Check />
          </IconButton>
        )}
        {!credentials && mode !== 'create' && (
          <IconButton onClick={() => generateCredentials(organization.id)}>
            <Cancel />
          </IconButton>
        )}
        {(actualItem.item !== organization.id || !actualItem.edit) && mode === 'update' && (
          <Typography className={classes.itemTitle}>
            {tmpOrganization && toUpper(tmpOrganization.name)}
          </Typography>
        )}
        {((actualItem.item === organization.id && actualItem.edit) || mode === 'create') && (
          <FormControl fullWidth className={classes.formControl} sx={{ mr: 2 }}>
            <TextField
              label="ID(*)"
              error={submited && (!tmpOrganization || !tmpOrganization.name)}
              value={name || ''}
              onChange={(event) =>
                setTmpOrganization({
                  ...tmpOrganization,
                  name: toUpper(event.target.value),
                })
              }
            />
          </FormControl>
        )}
        {((actualItem.item === organization.id && actualItem.edit) || mode === 'create') && (
          <FormControl fullWidth className={classes.formControl}>
            <TextField
              label="Nombre(*)"
              error={submited && (!tmpOrganization || !tmpOrganization.displayName)}
              value={displayName || ''}
              onChange={(event) =>
                setTmpOrganization({
                  ...tmpOrganization,
                  displayName: toUpper(event.target.value),
                })
              }
            />
          </FormControl>
        )}
        <Box ml="auto" display="flex">
          {mode === 'update' && (
            <IconButton onClick={() => handleShow(organization.id)} size="large">
              <Visibility />
            </IconButton>
          )}
          {mode === 'update' && (
            <IconButton onClick={() => handleEdit(organization.id)} size="large">
              <Create />
            </IconButton>
          )}
          {(!areEqual || mode === 'create') && !inProgress && (
            <Box mx={2}>
              <Button
                className={classes.button}
                variant="contained"
                color="primary"
                onClick={onSubmit}
              >
                Guardar
              </Button>
            </Box>
          )}
          {(!areEqual || mode === 'create') && inProgress && (
            <CircularProgress className={classes.circularProgress} />
          )}
        </Box>
      </Box>
      {mode === 'update' && actualItem.show && (
        <Box display="flex">
          <Typography className={`${classes.serviceTag} ${classes.serviceHealthSoftware}`}>
            Sistema de salud ({services && services.healthSoftware ? 'SI' : 'NO'})
          </Typography>
          <Typography className={`${classes.serviceTag} ${classes.serviceHealthSoftware}`}>
            Nuevo modelo ({billingData && billingData.isNewModel ? 'SI' : 'NO'})
          </Typography>
          <Typography className={`${classes.serviceTag} ${classes.serviceHealthSoftware}`}>
            Practicas ({services && services.practices ? 'SI' : 'NO'})
          </Typography>
          {services && services.notifications && (
            <Typography className={`${classes.serviceTag} ${classes.serviceNotifications}`}>
              Notificaciones
              {tmpOrganization?.stoppedNotificationsService
                ? ' (DETENIDAS)'
                : daysPriorForDelivery && ` (${daysPriorForDelivery * 24} hs previas)`}
            </Typography>
          )}
        </Box>
      )}
      {((mode === 'update' && actualItem.edit) || mode === 'create') && (
        <Box>
          <Box display="flex" alignItems="center">
            <Typography sx={{ mr: 4 }}>Sistema de salud</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!(services && services.healthSoftware)}
                  onChange={() => {
                    updateField('services.healthSoftware', false);
                    updateField('healthSoftware.name', 'decisionTree');
                  }}
                />
              }
              name="scope"
              label="NO"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!(services && services.healthSoftware)}
                  onChange={() => {
                    updateField('services.healthSoftware', true);
                  }}
                />
              }
              name="scope"
              label="SI"
            />
          </Box>
          <Box display="flex" alignItems="center">
            <Typography sx={{ mr: 4 }}>Nuevo modelo</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!(billingData && billingData.isNewModel)}
                  onChange={() => {
                    updateField('billingData.isNewModel', false);
                  }}
                />
              }
              name="scope"
              label="NO"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!(billingData && billingData.isNewModel)}
                  onChange={() => {
                    updateField('billingData.isNewModel', true);
                  }}
                />
              }
              name="scope"
              label="SI"
            />
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Typography sx={{ mr: 4 }}>Servicios</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!(services && services.practices)}
                  onChange={(value) => {
                    updateField('services.practices', value.target.checked);
                  }}
                />
              }
              name="scope"
              label="Practicas"
            />
            <Box display="flex" alignItems="center" sx={{ mr: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!(services && services.notifications)}
                    onChange={(value) => {
                      updateField('services.notifications', value.target.checked);
                    }}
                  />
                }
                name="scope"
                label="Notificaciones"
              />
              {services?.notifications && daysPriorForDelivery && (
                <Chip
                  label={`${daysPriorForDelivery * 24} horas previas`}
                  className={classes.itemValue}
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
            {services?.notifications && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!deliveryOnWeekend}
                      onChange={(value) => {
                        updateField(
                          'businessRules.notificationsRules.deliveryOnWeekend',
                          value.target.checked,
                        );
                      }}
                    />
                  }
                  name="scope"
                  label="Enviar notificaciones los fines de semana"
                  sx={{ mr: 2, ml: 2 }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, ml: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          !!(
                            businessRules &&
                            businessRules.notificationsRules &&
                            businessRules.notificationsRules.automatedCancelationApps
                          )
                        }
                        onChange={(value) =>
                          updateField(
                            'businessRules.notificationsRules.automatedCancelationApps',
                            value.target.checked,
                          )
                        }
                      />
                    }
                    label="Cancelar automáticamente turnos de notificaciones no respondidas tras"
                    sx={{ mr: 1, ml: 0 }}
                  />
                  <Select
                    value={businessRules?.notificationsRules?.cancellationOffset || 24}
                    onChange={(e) =>
                      updateField(
                        'businessRules.notificationsRules.cancellationOffset',
                        e.target.value,
                      )
                    }
                    size="small"
                    sx={{ minWidth: 60, mr: 1 }}
                  >
                    <MenuItem value={12}>12h</MenuItem>
                    <MenuItem value={24}>24h</MenuItem>
                    <MenuItem value={48}>48h</MenuItem>
                  </Select>
                  <Typography variant="body">sin respuesta</Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
      {mode === 'update' && actualItem.show && (
        <Box>
          <Box>
            <Typography className={classes.sectionTitle}>
              <b>Configuración dialogflow</b>
            </Typography>
            <Box mb={1}>
              <Typography>
                Dialogflow ID:
                {provider.dfId && <span className={classes.itemValue}>{provider.dfId}</span>}
              </Typography>
            </Box>
            <Typography>
              Deshabilitar mensajes desde el panel:{' '}
              <span className={classes.itemValue}>
                {businessRules?.disablePanelUserMessaging ? 'Sí' : 'No'}
              </span>
            </Typography>
            <Typography>
              Mis chats:
              <span className={classes.itemValue}>
                {businessRules && businessRules.misChats ? 'SI' : 'NO'}
              </span>
            </Typography>
            <Typography>
              Envio de imagenes:
              <span className={classes.itemValue}>
                {businessRules && businessRules.sendImages ? 'SI' : 'NO'}
              </span>
            </Typography>
            <Typography>
              Recibe imagenes:
              <span className={classes.itemValue}>
                {businessRules && businessRules.receivesImages ? 'SI' : 'NO'}
              </span>
            </Typography>
            <Typography>
              Recibe audio:
              <span className={classes.itemValue}>
                {businessRules && businessRules.receivesAudio ? 'SI' : 'NO'}
              </span>
            </Typography>
            <Typography>
              Recibe archivos:
              <span className={classes.itemValue}>
                {businessRules && businessRules.receivesFiles ? 'SI' : 'NO'}
              </span>
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.sectionTitle}>
              <b>Configuración gupshup</b>
            </Typography>
            <Box mb={1}>
              <Typography>
                Nombre de aplicación:
                {provider.appName && <span className={classes.itemValue}>{provider.appName}</span>}
              </Typography>
              <Typography>
                Telefono:
                {provider.phone && <span className={classes.itemValue}>{provider.phone}</span>}
              </Typography>
            </Box>
          </Box>
          {services && services.healthSoftware && (
            <>
              <Typography className={classes.sectionTitle}>
                <b>Integración con software de salud</b>
              </Typography>
              <Box mb={1}>
                <Typography>
                  Api URL:{' '}
                  {healthSoftware && healthSoftware.apiURL && (
                    <span className={classes.itemValue}>{healthSoftware.apiURL}</span>
                  )}
                </Typography>
                <Typography>
                  Implementación:{' '}
                  {healthSoftware && healthSoftware.name && (
                    <span className={classes.itemValue}>{healthSoftware.name}</span>
                  )}
                </Typography>
                <Typography>
                  Institution ID:{' '}
                  {healthSoftware && healthSoftware.institutionId && (
                    <span className={classes.itemValue}>{healthSoftware.institutionId}</span>
                  )}
                </Typography>
                <Typography>
                  Subsidiary ID:{' '}
                  {healthSoftware && healthSoftware.subsidiaryId && (
                    <span className={classes.itemValue}>{healthSoftware.subsidiaryId}</span>
                  )}
                </Typography>
              </Box>
              <Typography className={classes.sectionTitle}>
                <b>Reglas de negocio</b>
              </Typography>
              <Box mb={1}>
                <Typography>
                  Mostrar turnos despues de:
                  {businessRules.fromThisDayOn && businessRules.fromThisDayOn !== 0 ? (
                    <span className={classes.itemValue}>{businessRules.fromThisDayOn}</span>
                  ) : (
                    ''
                  )}
                  {businessRules.fromThisDayOn === 0 ? ' hoy' : ''}
                  {businessRules.fromThisDayOn && businessRules.fromThisDayOn === 1 ? ' (dia)' : ''}
                  {businessRules.fromThisDayOn && businessRules.fromThisDayOn > 1 ? ' (dias)' : ''}
                </Typography>
                <Typography>
                  Maximo turnos en grilla:
                  {businessRules.maxAppsOnGrid && (
                    <span className={classes.itemValue}>{businessRules.maxAppsOnGrid}</span>
                  )}
                </Typography>
                <Typography>
                  Límite de respuesta (horas):
                  {businessRules.responseLimit && (
                    <span className={classes.itemValue}>{businessRules.responseLimit}</span>
                  )}
                </Typography>
                <Typography>
                  Maximo turnos en grilla por dia:
                  {businessRules.maxAppsPerDay && (
                    <span className={classes.itemValue}>{businessRules.maxAppsPerDay}</span>
                  )}
                </Typography>
                <Typography>
                  Turnos sin ficha:
                  <span className={classes.itemValue}>
                    {businessRules && businessRules.pacienteSinFicha ? 'SI' : 'NO'}
                  </span>
                </Typography>
                <Typography className={classes.sectionTitle}>
                  <b>Notificaciones</b>
                </Typography>
                <Typography>
                  Plantilla alta:
                  <span className={classes.itemValue}>
                    {services && services.altaTemplate ? 'SI' : 'NO'}
                  </span>
                </Typography>
                <Typography>
                  Plantilla activación:
                  <span className={classes.itemValue}>
                    {services && services.activationTemplate ? 'SI' : 'NO'}
                  </span>
                </Typography>
              </Box>
            </>
          )}
        </Box>
      )}
      {(actualItem.edit || mode === 'create') && (
        <Box>
          <Box>
            <Typography className={classes.sectionTitle}>
              <b>Configuración dialogflow</b>
            </Typography>
            <Box mb={1}>
              <FormControl fullWidth className={classes.formControl}>
                <TextField
                  label="Dialogflow ID(*)"
                  value={provider && provider.dfId ? provider.dfId : ''}
                  error={
                    submited &&
                    (!tmpOrganization ||
                      !tmpOrganization.provider ||
                      !trim(tmpOrganization.provider.dfId))
                  }
                  onChange={(event) => updateField('provider.dfId', event.target.value)}
                />
              </FormControl>
            </Box>
            <Box mb={1}>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!businessRules?.disablePanelUserMessaging}
                        onChange={(value) =>
                          updateField(
                            'businessRules.disablePanelUserMessaging',
                            value.target.checked,
                          )
                        }
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center">
                        Deshabilitar mensajes desde el panel
                        <Tooltip title="Cuando está activo, los usuarios del panel (no admins) no podrán enviar mensajes, respuestas ni plantillas.">
                          <IconButton size="small" sx={{ ml: 1 }}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                </FormGroup>
              </FormControl>
            </Box>
            <Box mb={1}>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!(businessRules && businessRules.misChats)}
                        onChange={(value) =>
                          updateField('businessRules.misChats', value.target.checked)
                        }
                      />
                    }
                    label="Mis Chats"
                  />
                </FormGroup>
              </FormControl>
            </Box>
            <Box mb={1}>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!(businessRules && businessRules.sendImages)}
                        onChange={(value) =>
                          updateField('businessRules.sendImages', value.target.checked)
                        }
                      />
                    }
                    label="Envio de imagenes"
                  />
                </FormGroup>
              </FormControl>
            </Box>
            <Box mb={1}>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!(businessRules && businessRules.receivesImages)}
                        onChange={(value) =>
                          updateField('businessRules.receivesImages', value.target.checked)
                        }
                      />
                    }
                    label="Recibe imagenes"
                  />
                </FormGroup>
              </FormControl>
            </Box>
            <Box mb={1}>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!(businessRules && businessRules.receivesAudio)}
                        onChange={(value) =>
                          updateField('businessRules.receivesAudio', value.target.checked)
                        }
                      />
                    }
                    label="Recibe audio"
                  />
                </FormGroup>
              </FormControl>
            </Box>
            <Box mb={1}>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!(businessRules && businessRules.receivesFiles)}
                        onChange={(value) =>
                          updateField('businessRules.receivesFiles', value.target.checked)
                        }
                      />
                    }
                    label="Recibe archivos"
                  />
                </FormGroup>
              </FormControl>
            </Box>
          </Box>
          <Box>
            <Typography className={classes.sectionTitle}>
              <b>Configuración gupshup</b>
            </Typography>
            <Box mb={1}>
              <FormControl fullWidth className={classes.formControl}>
                <TextField
                  label="Nombre de aplicación"
                  value={provider && provider.appName ? provider.appName : ''}
                  onChange={(event) => updateField('provider.appName', event.target.value)}
                />
              </FormControl>
            </Box>
            <Box mb={1}>
              <FormControl fullWidth className={classes.formControl}>
                <TextField
                  label="Telefono"
                  value={provider && provider.phone ? provider.phone : ''}
                  onChange={(event) => updateField('provider.phone', event.target.value)}
                />
              </FormControl>
            </Box>
          </Box>
          {services && services.healthSoftware && (
            <>
              <Typography className={classes.sectionTitle}>
                <b>Integración con software de salud</b>
              </Typography>
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <Autocomplete
                    size="small"
                    freeSolo
                    value={healthSoftware && healthSoftware.name ? healthSoftware.name : ''}
                    options={['mrTurno5', 'macena']}
                    onChange={(event, value) => {
                      if (!services.healthSoftware) value = 'decisionTree';
                      updateField('healthSoftware.name', value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={
                          submited &&
                          services &&
                          services.healthSoftware &&
                          (!tmpOrganization ||
                            !tmpOrganization.healthSoftware ||
                            !trim(tmpOrganization.healthSoftware.name))
                        }
                        label="Implementación (mrTurno4 - mrTurno5)"
                        name="typeValue"
                        onChange={(event) => updateField('healthSoftware.name', event.target.value)}
                      />
                    )}
                  />
                </FormControl>
              </Box>
              {tmpOrganization?.healthSoftware?.name === 'macena' && (
                <Box mb={1}>
                  <FormControl fullWidth className={classes.formControl}>
                    <TextField
                      label="API URL"
                      value={healthSoftware && healthSoftware.apiURL ? healthSoftware.apiURL : ''}
                      onChange={(event) => updateField('healthSoftware.apiURL', event.target.value)}
                    />
                  </FormControl>
                </Box>
              )}
              {tmpOrganization &&
                tmpOrganization.healthSoftware &&
                tmpOrganization.healthSoftware.name === 'mrTurno5' && (
                  <>
                    <Box mb={1}>
                      <FormControl fullWidth className={classes.formControl}>
                        <TextField
                          label="Sub"
                          value={(healthSoftware && healthSoftware.sub) || ''}
                          onChange={(event) =>
                            updateField('healthSoftware.sub', event.target.value)
                          }
                        />
                      </FormControl>
                    </Box>
                    <Box mb={1}>
                      <FormControl fullWidth className={classes.formControl}>
                        <TextField
                          label="Institution ID"
                          value={(healthSoftware && healthSoftware.institutionId) || ''}
                          onChange={(event) =>
                            updateField('healthSoftware.institutionId', event.target.value)
                          }
                        />
                      </FormControl>
                    </Box>
                    <Box mb={1}>
                      <FormControl fullWidth className={classes.formControl}>
                        <TextField
                          label="Subsidiary ID"
                          value={(healthSoftware && healthSoftware.subsidiaryId) || ''}
                          onChange={(event) =>
                            updateField('healthSoftware.subsidiaryId', event.target.value)
                          }
                        />
                      </FormControl>
                    </Box>
                  </>
                )}
              {tmpOrganization &&
                tmpOrganization.healthSoftware &&
                tmpOrganization.healthSoftware.name === 'macena' && (
                  <>
                    <Box mb={1}>
                      <FormControl fullWidth className={classes.formControl}>
                        <TextField
                          label="userName"
                          value={(healthSoftware && healthSoftware.userName) || ''}
                          onChange={(event) =>
                            updateField('healthSoftware.userName', event.target.value)
                          }
                        />
                      </FormControl>
                    </Box>
                    <Box mb={1}>
                      <FormControl fullWidth className={classes.formControl}>
                        <TextField
                          label="password"
                          value={(healthSoftware && healthSoftware.password) || ''}
                          onChange={(event) =>
                            updateField('healthSoftware.password', event.target.value)
                          }
                        />
                      </FormControl>
                    </Box>
                  </>
                )}
              <Typography className={classes.sectionTitle}>
                <b>Reglas de negocio</b>
              </Typography>
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <TextField
                    label="Mostrar turnos despues de (dias)"
                    type="number"
                    value={
                      businessRules && businessRules.fromThisDayOn ? businessRules.fromThisDayOn : 0
                    }
                    onChange={(event) => {
                      if (parseInt(event.target.value) !== 0) {
                        updateField('businessRules.afterThisTimeHours', 0);
                        updateField('businessRules.afterThisTimeMinutes', 0);
                        updateField('businessRules.fromThisDayOn', parseInt(event.target.value));
                      } else {
                        updateField('businessRules.fromThisDayOn', parseInt(event.target.value));
                      }
                    }}
                  />
                </FormControl>
              </Box>
              {(businessRules?.fromThisDayOn === 0 ||
                tmpOrganization?.businessRules.fromThisDayOn === 0) && (
                <Box mb={1}>
                  <FormControl fullWidth className={classes.formControl}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          label="Horas"
                          type="number"
                          fullWidth
                          value={
                            businessRules?.afterThisTimeHours &&
                            (businessRules?.fromThisDayOn < 1 ||
                              tmpOrganization?.businessRules.fromThisDayOn < 1)
                              ? businessRules.afterThisTimeHours > 23
                                ? 23
                                : businessRules.afterThisTimeHours < 0
                                ? 0
                                : businessRules.afterThisTimeHours
                              : 0
                          }
                          onChange={(event) => {
                            if (parseInt(event.target.value) > 23) {
                              updateField('businessRules.afterThisTimeHours', parseInt(23));
                            }
                            if (parseInt(event.target.value) < 0) {
                              updateField('businessRules.afterThisTimeHours', parseInt(0));
                            }
                            if (
                              parseInt(event.target.value) <= 23 ||
                              parseInt(event.target.value) >= 0
                            ) {
                              updateField(
                                'businessRules.afterThisTimeHours',
                                parseInt(event.target.value),
                              );
                            }
                          }}
                          InputProps={{ inputProps: { min: 0, max: 23 } }} // Limitar entre 0 y 23 horas
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Minutos"
                          type="number"
                          fullWidth
                          value={
                            businessRules?.afterThisTimeMinutes
                              ? businessRules.afterThisTimeMinutes > 59
                                ? 59
                                : businessRules.afterThisTimeMinutes < 0
                                ? 0
                                : businessRules.afterThisTimeMinutes
                              : 0
                          }
                          onChange={(event) => {
                            if (parseInt(event.target.value) > 59) {
                              updateField('businessRules.afterThisTimeMinutes', parseInt(59));
                            }
                            if (parseInt(event.target.value) < 0) {
                              updateField('businessRules.afterThisTimeMinutes', parseInt(0));
                            }
                            if (
                              parseInt(event.target.value) <= 59 ||
                              parseInt(event.target.value) >= 0
                            ) {
                              updateField(
                                'businessRules.afterThisTimeMinutes',
                                parseInt(event.target.value),
                              );
                            }
                          }}
                          inputProps={{ inputProps: { min: 0, max: 59 } }} // Limitar entre 0 y 59 minutos
                        />
                      </Grid>
                    </Grid>
                  </FormControl>
                </Box>
              )}

              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <TextField
                    label="Maximo turnos en grilla"
                    type="number"
                    value={
                      businessRules && businessRules.maxAppsOnGrid ? businessRules.maxAppsOnGrid : 0
                    }
                    onChange={(event) =>
                      updateField('businessRules.maxAppsOnGrid', parseInt(event.target.value))
                    }
                  />
                </FormControl>
              </Box>
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <TextField
                    label="  (horas)"
                    type="number"
                    value={
                      businessRules && businessRules.responseLimit ? businessRules.responseLimit : 0
                    }
                    onChange={(event) =>
                      updateField('businessRules.responseLimit', parseInt(event.target.value))
                    }
                  />
                </FormControl>
              </Box>
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <TextField
                    label="Maximo turnos en grilla por dia"
                    type="number"
                    value={
                      businessRules && businessRules.maxAppsPerDay ? businessRules.maxAppsPerDay : 0
                    }
                    onChange={(event) =>
                      updateField('businessRules.maxAppsPerDay', parseInt(event.target.value))
                    }
                  />
                </FormControl>
              </Box>
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!(businessRules && businessRules.pacienteSinFicha)}
                          onChange={(value) =>
                            updateField('businessRules.pacienteSinFicha', value.target.checked)
                          }
                        />
                      }
                      label="Turnos sin ficha"
                    />
                  </FormGroup>
                </FormControl>
              </Box>
              <Box mb={1} display="flex" alignItems="center" gap={2}>
                <FormControl>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!businessRules?.gecrosValidation?.validate}
                          onChange={(value) =>
                            updateField(
                              'businessRules.gecrosValidation.validate',
                              value.target.checked,
                            )
                          }
                        />
                      }
                      label="Validar en Gecros"
                    />
                  </FormGroup>
                </FormControl>
                {businessRules?.gecrosValidation?.validate && (
                  <Button variant="contained" color="primary" onClick={handleOpenModal}>
                    Incluir obras sociales
                  </Button>
                )}
              </Box>
              <InsuranceSelectorModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveModal}
                initialIncludedInsurances={tmpOrganization?.businessRules?.gecrosValidation || []}
                organizationId={tmpOrganization?.id}
              />
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!(businessRules && businessRules.lastPatientInsurance)}
                          onChange={(value) =>
                            updateField('businessRules.lastPatientInsurance', value.target.checked)
                          }
                        />
                      }
                      label="Usar última obra social utilizada"
                    />
                  </FormGroup>
                </FormControl>
              </Box>
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!businessRules?.filterOsPlan}
                          onChange={(value) =>
                            updateField('businessRules.filterOsPlan', value.target.checked)
                          }
                        />
                      }
                      label="Filtrar planes disponibles para WhatsApp"
                    />
                  </FormGroup>
                </FormControl>
              </Box>
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!businessRules?.offerParticular}
                          onChange={(value) =>
                            updateField('businessRules.offerParticular', value.target.checked)
                          }
                        />
                      }
                      label="Ofrecer particular cuando no hay turnos disponibles"
                    />
                  </FormGroup>
                </FormControl>
              </Box>
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            !!(
                              businessRules &&
                              businessRules.notificationsRules &&
                              businessRules.notificationsRules.filterExtraAppointments
                            )
                          }
                          onChange={(value) =>
                            updateField(
                              'businessRules.notificationsRules.filterExtraAppointments',
                              value.target.checked,
                            )
                          }
                        />
                      }
                      label="Filtrar sobreturnos de recordatorios"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            !!(
                              businessRules &&
                              businessRules.notificationsRules &&
                              businessRules.notificationsRules.redeliveryReminders
                            )
                          }
                          onChange={(value) =>
                            updateField(
                              'businessRules.notificationsRules.redeliveryReminders',
                              value.target.checked,
                            )
                          }
                        />
                      }
                      label="Reenvío de recordatorios no contestados"
                    />
                  </FormGroup>
                </FormControl>
              </Box>
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!(businessRules && businessRules.institucionalMessageMedia)}
                          onChange={(value) =>
                            updateField(
                              'businessRules.institucionalMessageMedia',
                              value.target.checked,
                            )
                          }
                        />
                      }
                      label="Permitir multimedia en mensajes intitucionales"
                    />
                  </FormGroup>
                </FormControl>
              </Box>
              <Box mb={1}>
                <FormControl fullWidth className={classes.formControl}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!(businessRules && businessRules.useAlternativePlanName)}
                          onChange={(value) =>
                            updateField(
                              'businessRules.useAlternativePlanName',
                              value.target.checked,
                            )
                          }
                        />
                      }
                      label="Mostrar nombre alternativo de plan"
                    />
                    ...
                  </FormGroup>
                </FormControl>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

const Props = {
  organization: propTypes.object,
  mode: propTypes.string,
  actualItem: propTypes.object,
  handleShow: propTypes.func,
  handleEdit: propTypes.func,
  handleSubmit: propTypes.func,
  organizationNames: propTypes.array,
  credentials: propTypes.string,
  generateCredentials: propTypes.func,
};

Organization.propTypes = Props;

export default Organization;
